/**
 * UpdateConditionStatusModal
 *
 * Modal for transitioning a condition's status. Shows current status and
 * allows selecting a new status. Auto-sets satisfiedDate when status = 'satisfied'.
 *
 * Updates via PATCH /api/conditions/:id/status
 * F007 AC003
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "outstanding", label: "Outstanding" },
  { value: "submitted", label: "Submitted" },
  { value: "under_review", label: "Under Review" },
  { value: "satisfied", label: "Satisfied" },
  { value: "waived", label: "Waived" },
  { value: "not_applicable", label: "Not Applicable" },
];

const CONDITION_STATUS_COLORS: Record<string, string> = {
  outstanding: "bg-amber-100 text-amber-800",
  submitted: "bg-blue-100 text-blue-800",
  under_review: "bg-yellow-100 text-yellow-800",
  satisfied: "bg-green-100 text-green-800",
  waived: "bg-gray-100 text-gray-800",
  not_applicable: "bg-gray-100 text-gray-500",
};

const CONDITION_STATUS_LABELS: Record<string, string> = {
  outstanding: "Outstanding",
  submitted: "Submitted",
  under_review: "Under Review",
  satisfied: "Satisfied",
  waived: "Waived",
  not_applicable: "N/A",
};

// ── Types ────────────────────────────────────────────────────────────────────

interface ConditionRow {
  id: string;
  applicationId: string;
  conditionType: string;
  conditionCategory: string;
  conditionDescription: string;
  conditionShortName: string | null;
  conditionStatus: string;
  conditionNumber: number | null;
  responsibleParty: string | null;
  priority: number | null;
  dueDate: string | null;
  satisfiedDate: string | null;
  satisfiedBy: string | null;
  lenderFeedback: string | null;
  brokerNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UpdateConditionStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  condition: ConditionRow;
  applicationId: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export function UpdateConditionStatusModal({
  open,
  onOpenChange,
  condition,
  applicationId,
}: UpdateConditionStatusModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [newStatus, setNewStatus] = useState(condition.conditionStatus);
  const [lenderFeedback, setLenderFeedback] = useState(condition.lenderFeedback || "");

  // Reset form state when condition changes or modal opens
  useEffect(() => {
    if (open) {
      setNewStatus(condition.conditionStatus);
      setLenderFeedback(condition.lenderFeedback || "");
    }
  }, [open, condition]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/conditions/${condition.id}/status`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/applications/${applicationId}/conditions`],
      });
      toast({ title: "Condition status updated" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update condition status",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    updateMutation.mutate({
      status: newStatus,
      lenderFeedback: lenderFeedback.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[450px]"
        data-testid="dialog-update-condition-status"
      >
        <DialogHeader>
          <DialogTitle>Update Condition Status</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Condition description */}
          <div>
            <p className="text-sm text-muted-foreground">Condition:</p>
            <p className="text-sm font-medium">{condition.conditionDescription}</p>
          </div>

          {/* Current status */}
          <div>
            <p className="text-sm text-muted-foreground mb-1">Current Status:</p>
            <Badge
              variant="secondary"
              className={`text-xs ${
                CONDITION_STATUS_COLORS[condition.conditionStatus] || ""
              }`}
            >
              {CONDITION_STATUS_LABELS[condition.conditionStatus] ||
                condition.conditionStatus}
            </Badge>
          </div>

          {/* New status */}
          <div className="space-y-1.5">
            <Label>New Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="select-new-status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Satisfied note */}
          {newStatus === "satisfied" && (
            <p className="text-xs text-green-600">
              Satisfied date will be set automatically to today.
            </p>
          )}

          {/* Lender Feedback */}
          <div className="space-y-1.5">
            <Label>Lender Feedback</Label>
            <Textarea
              placeholder="Optional feedback from lender..."
              value={lenderFeedback}
              onChange={(e) => setLenderFeedback(e.target.value)}
              rows={3}
              data-testid="input-lender-feedback"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
            data-testid="button-cancel-status-update"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateMutation.isPending || newStatus === condition.conditionStatus}
            data-testid="button-submit-status-update"
          >
            {updateMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Update Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
